import { toast } from 'react-toastify'
import { CreateRecordBatchRequest } from '../../types'
import { last } from 'lodash'
import { FileWithPath } from 'react-dropzone'
import numeral from 'numeral'
import { PrimaryButton, SecondaryOutlineButton } from '../ui/Button'
import {
  FailedToCreateFileRecordError,
  FailedToCreateFolderError,
  MissingAuthTokenError,
  FailedToUploadFileError,
} from '../../errors'
import {
  createFolderFromPath,
  getFileRecordsAndSignedUrls,
  getUserInfo,
  removeLocalFileRecordResultAsync,
  saveFileRecordsToLocalResultAsync,
  uploadFileResultAsync,
} from '../../utils/upload-util'
import {
  createFileRecordBatchResultAsync,
  deleteFoldersResultAsync,
} from '../../utils/file_fp'
import { err, ok, Result, ResultAsync } from 'neverthrow'
import { ExtractlyOfficeApiErrorClass } from '../../errors/office_api_errors'
import { useNavigate } from 'react-router-dom'

type SignedUrl = {
  signedUrl: string
  id: string
}

type Props = {
  files: FileWithPath[]
  setFiles: (files: FileWithPath[]) => void
  setFolderSet: (folders: string[]) => void
  acceptedFiles: File[]
  folderStack?: { id: string }[]
  setUpload: (upload: boolean) => void
  refetchFiles: () => Promise<void>
  setLoading: (loading: boolean) => void
  setUploading: (loading: boolean) => void
}

type UploadDomainError =
  | MissingAuthTokenError
  | ExtractlyOfficeApiErrorClass
  | FailedToCreateFolderError
  | FailedToCreateFileRecordError
  | FailedToUploadFileError
  | { message: string; _tag?: 'InvalidFilesSelectionError' }

const filesSanityCheck = (files: FileWithPath[]) => {
  const allowedFileTypes = new Set([
    'application/pdf',
    'image/jpeg',
    'image/png',
  ])
  return (
    files.every((file) => file.size < numeral('15MB').value()!) &&
    files.every((file) => allowedFileTypes.has(file.type))
  )
}

const UploadFooter = ({
  acceptedFiles,
  files,
  refetchFiles,
  setFiles,
  setFolderSet,
  setLoading,
  setUpload,
  folderStack,
  setUploading,
}: Props) => {
  const navigate = useNavigate()

  const errorFallback = (error: UploadDomainError, customMessage?: string) => {
    if (customMessage) {
      toast.error(customMessage)
    } else if (error instanceof MissingAuthTokenError) {
      toast.error('Please sign in again, redirect in 3 seconds')
      setTimeout(() => navigate(0), 3000)
      return
    } else if (error instanceof ExtractlyOfficeApiErrorClass) {
      if (error.code === 'InvalidOperationInCellEditMode') {
        toast.error('Exit cell edit mode to continue')
      } else if (error.code === 'RequestAborted') {
        toast.error('Request aborted. Save the file and try again')
      } else {
        toast.error(`Office error: ${error.message}`)
      }
    } else if (error instanceof FailedToCreateFolderError) {
      toast.error('Could not create folders')
    } else if (error instanceof FailedToCreateFileRecordError) {
      toast.error('Could not create file records')
    } else if (error instanceof FailedToUploadFileError) {
      toast.error('Could not upload file(s)')
    } else if ('_tag' in error && error._tag === 'InvalidFilesSelectionError') {
      toast.error(error.message)
    } else {
      toast.error(error.message || 'Upload failed')
    }
    setUploading(false)
    setLoading(false)
  }

  const preUploadProcess = async (
    fileRecords: string[]
  ): Promise<
    Result<
      SignedUrl[],
      | ExtractlyOfficeApiErrorClass
      | MissingAuthTokenError
      | FailedToCreateFolderError
      | FailedToCreateFileRecordError
    >
  > => {
    if (!files.length) return ok([])

    const userInfo = await getUserInfo()

    if (userInfo.isErr()) {
      return err(userInfo.error)
    }

    const { oid } = userInfo.value

    const parentFolderId = last(folderStack)?.id

    const filteredFiles = files.filter((file) => file.path?.split('/'))

    const paths = filteredFiles.map((file) => file.path!)
    /**
     * Create folder from paths
     */
    const createFolderResult = await createFolderFromPath(
      paths,
      oid,
      parentFolderId
    )
    if (createFolderResult.isErr()) {
      const e = createFolderResult.error
      const deleteFoldersResult = await deleteFoldersResultAsync(e)
      if (deleteFoldersResult.isErr()) {
        errorFallback(
          new FailedToCreateFolderError(),
          'Could not create or clean up folders'
        )
      } else {
        errorFallback(
          new FailedToCreateFolderError(),
          'Could not create folders (cleaned up)'
        )
      }
      return err(new FailedToCreateFolderError())
    }
    const [fileRecord, parentFolderIdItems] = createFolderResult.value
    /**
     * Create record batch
     */
    const payload: CreateRecordBatchRequest['files'] = filteredFiles.map(
      (file) => {
        const arr = file.path?.split('/')!

        const parentFolderId = parentFolderIdItems.find(
          (f) => f.path === file.path
        )?.parentFolderId
        return {
          parentFolderId: parentFolderId ?? null,
          fileName: file.name,
          dir: arr.length <= 1 ? `/${file.path}` : file.path!,
          contentType: file.type,
        }
      }
    )

    const createFileRecordResult = await createFileRecordBatchResultAsync(
      payload
    )

    if (createFileRecordResult.isErr()) {
      errorFallback(createFileRecordResult.error)
      return err(createFileRecordResult.error)
    }

    const results = createFileRecordResult.value

    const [records, signedUrls] = getFileRecordsAndSignedUrls(results, oid)

    const allRecords = [...fileRecord, ...records]
    fileRecords.push(...allRecords.map((f) => f.fileId))
    const saveResult = await saveFileRecordsToLocalResultAsync(allRecords)

    if (saveResult.isErr()) {
      errorFallback(saveResult.error, 'Could not save files locally')
      return err(saveResult.error)
    }

    return ok(signedUrls)
  }

  const uploadFiles = async () => {
    setLoading(true)
    setUploading(true)
    const fileRecords: string[] = []

    const checked = filesSanityCheck(files)
    if (!checked) {
      errorFallback({
        message: 'Files must be PDF, JPEG, or PNG under 15MB',
        _tag: 'InvalidFilesSelectionError',
      })
      return
    }

    const result = await preUploadProcess(fileRecords)

    if (result.isErr()) {
      errorFallback(result.error)
      return
    }

    const signedUrls = result.value

    const asyncResults = signedUrls
      .map((signedUrlObj, i) => {
        const file = files[i]
        return [signedUrlObj, file] as const
      })
      .map(([signedUrlObj, file]) => uploadFileResultAsync(signedUrlObj, file))

    const asyncResponse = await ResultAsync.combine(asyncResults)

    if (asyncResponse.isErr()) {
      const deleteRecordResult = await removeLocalFileRecordResultAsync(
        fileRecords
      )
      if (deleteRecordResult.isErr()) {
        errorFallback(
          asyncResponse.error,
          `Upload failed and could not clean up: ${deleteRecordResult.error.message}`
        )
      } else {
        errorFallback(
          asyncResponse.error,
          'Upload failed (cleaned up local records)'
        )
      }
      return
    }

    setUploading(false)
    setLoading(false)
    setUpload(false)
    await refetchFiles()
  }

  const removeAllFiles = () => {
    acceptedFiles.length = 0
    acceptedFiles.splice(0, acceptedFiles.length)
    setFiles([])
    setFolderSet([])
  }
  return (
    <footer className="absolute flex justify-end  bottom-0 bg-white w-full rounded-t-3xl h-[60px]">
      <div className="flex p-2">
        <SecondaryOutlineButton onClick={removeAllFiles}>
          Cancel
        </SecondaryOutlineButton>

        <PrimaryButton onClick={uploadFiles}>Process</PrimaryButton>
      </div>
    </footer>
  )
}

export default UploadFooter
